import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { Workspace } from '@/components/WorkspaceTable/WorkspaceTable';
import {
  getUserWorkspaces,
  getWorkspaceImages,
  getWorkspaceProgress,
  getWorkspaceVersions,
  restartWorkspace,
  startWorkspace,
  stopWorkspace,
  terminateWorkspace,
  transformWorkspacesForUI,
} from '@/lib/api/rome';
import { beginWorkspaceAuthorization, openWorkspaceAuthWindow, WorkspaceAuthError } from '@/lib/auth/workspaceAuth';
import { StorageKeys } from '@/lib/persistence';
import { clearAllContexts, updateWorkspaceContext } from '@/lib/services/sentryEnrichment';
import {
  type AntiochWorkspace,
  type RomeStartWorkspaceResponse,
  type RomeWorkspaceStartResponse,
  type WorkspaceAuthPendingResponse,
  type WorkspaceImage,
  type WorkspaceProgressStep,
  WorkspaceState,
  type WorkspaceVersion,
} from '@/lib/types/rome';
import type { WorkspaceStreamTarget } from '@/lib/types/workspace';
import { captureOrBreadcrumbNetworkError } from '@/lib/utils/sentryFilters';
import {
  buildTargetFromWorkspace,
  getLocalStreamTarget,
  getLocalWorkspace,
  isLocalDevelopment,
  LOCAL_WORKSPACE_ID,
} from '@/lib/utils/workspace';
import { useSharedState } from './SharedStateProvider';

// Local workspace entry for development mode
const localWorkspace = getLocalWorkspace();

// Transitional state handling
const TRANSITIONAL_WORKSPACE_STATES = new Set<WorkspaceState>([
  WorkspaceState.STARTING,
  WorkspaceState.STOPPING,
  WorkspaceState.TERMINATING,
]);
const TRANSITIONAL_WORKSPACE_GRACE_MS = 30_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

type WorkspaceMutationResponse = { status: number; data: RomeWorkspaceStartResponse | null };

function getWorkspaceStartData(response: WorkspaceMutationResponse): RomeStartWorkspaceResponse['data'] | null {
  if (response.status !== 200 || response.data?.status_code !== 200) {
    return null;
  }
  return response.data.data as RomeStartWorkspaceResponse['data'];
}

function readSelectedWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(StorageKeys.SELECTED_WORKSPACE);
  } catch {
    return null;
  }
}

function writeSelectedWorkspaceId(workspaceId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(StorageKeys.SELECTED_WORKSPACE, workspaceId);
  } catch {
    // Persistence is best-effort; the live workspace selection is
    // already held in React state for this tab.
  }
}

function clearSelectedWorkspaceId(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(StorageKeys.SELECTED_WORKSPACE);
  } catch {
    // ignore
  }
}

/**
 * Get a numeric rank for workspace states to determine state progression.
 *
 * @param state - The workspace state to rank.
 * @return Numeric rank where higher values mean more progressed states.
 */
function workspaceStateRank(state: WorkspaceState): number {
  switch (state) {
    case WorkspaceState.STARTING:
      return 0;
    case WorkspaceState.RUNNING:
      return 1;
    case WorkspaceState.STOPPING:
      return 2;
    case WorkspaceState.STOPPED:
      return 3;
    case WorkspaceState.ERRORED:
      return 4;
    case WorkspaceState.TERMINATING:
      return 5;
    case WorkspaceState.TERMINATED:
      return 6;
    default:
      throw new Error(`Unknown WorkspaceState: ${state}`);
  }
}

/**
 * Merge server workspaces with local state to avoid UI state bounce.
 *
 * @param params - Merge parameters including previous, server, and timing data.
 * @return Merged workspace list sorted by creation time.
 */
function mergeWorkspaces(params: {
  prev: AntiochWorkspace[];
  server: AntiochWorkspace[];
  lastSeenMsById: Record<string, number>;
  optimisticMsById: Record<string, number>;
  nowMs: number;
}): AntiochWorkspace[] {
  const { prev, server, lastSeenMsById, optimisticMsById, nowMs } = params;

  const prevById = new Map(prev.map((w) => [w.id, w]));
  const mergedById = new Map<string, AntiochWorkspace>();

  // Start with server workspaces as source of truth, but avoid UI state bounce
  for (const serverWorkspace of server) {
    const prevWorkspace = prevById.get(serverWorkspace.id);
    if (!prevWorkspace) {
      mergedById.set(serverWorkspace.id, serverWorkspace);
      continue;
    }

    // Determine which state to show
    let state: WorkspaceState;

    // Check if this workspace had a recent optimistic update (start, stop, restart)
    // Only use the optimistic timestamp, not the poll timestamp
    const optimisticMs = optimisticMsById[prevWorkspace.id];
    const withinOptimisticGrace = optimisticMs !== undefined && nowMs - optimisticMs < TRANSITIONAL_WORKSPACE_GRACE_MS;

    // Track whether we're preserving an optimistic transitional state
    let preservingOptimistic = false;

    if (
      TRANSITIONAL_WORKSPACE_STATES.has(prevWorkspace.state) &&
      withinOptimisticGrace &&
      prevWorkspace.state !== serverWorkspace.state
    ) {
      // Preserve optimistic transitional state (STARTING, STOPPING, TERMINATING) within
      // the grace period, even if the server hasn't caught up yet. This handles restarts
      // (STOPPED->STARTING) where the rank comparison would incorrectly prefer STOPPED.
      state = prevWorkspace.state;
      preservingOptimistic = true;
    } else if (prevWorkspace.state === WorkspaceState.ERRORED && serverWorkspace.state === WorkspaceState.STARTING) {
      // Accept server-driven restart from ERRORED (e.g. triggered from another client/CLI)
      state = serverWorkspace.state;
    } else if (workspaceStateRank(prevWorkspace.state) > workspaceStateRank(serverWorkspace.state)) {
      state = prevWorkspace.state;
    } else {
      state = serverWorkspace.state;
    }

    // Preserve previous name if server returns empty (maintains optimistic UI)
    const name = serverWorkspace.name || prevWorkspace.name;
    // When preserving optimistic state (e.g. restart with version change), keep
    // the optimistic version too so it doesn't flicker back to the pre-restart value
    const version = preservingOptimistic
      ? prevWorkspace.version || serverWorkspace.version
      : serverWorkspace.version || prevWorkspace.version;

    mergedById.set(serverWorkspace.id, {
      ...serverWorkspace,
      state,
      name,
      version,
    });
  }

  // Keep transitional workspaces briefly even if they disappear from the server response
  for (const prevWorkspace of prev) {
    if (mergedById.has(prevWorkspace.id)) continue;

    const lastSeen = lastSeenMsById[prevWorkspace.id] ?? nowMs;
    const withinGrace = nowMs - lastSeen < TRANSITIONAL_WORKSPACE_GRACE_MS;

    if (TRANSITIONAL_WORKSPACE_STATES.has(prevWorkspace.state) && withinGrace) {
      mergedById.set(prevWorkspace.id, prevWorkspace);
    }
  }

  // Stable ordering: newest first, tiebreak by id to avoid jitter
  return Array.from(mergedById.values()).sort((a, b) => {
    if (a.createdAt !== b.createdAt) return b.createdAt - a.createdAt;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Value-equality over two workspace lists.
 *
 * ``AntiochWorkspace`` is a flat record of primitives, so a field-by-field
 * comparison in array order is exhaustive — every server-observable change is
 * caught. Used to keep the previous array reference when a poll produced no
 * effective change, so the context value identity stays stable and consumers
 * don't re-render on no-op refreshes.
 *
 * @param a - Previous workspace list.
 * @param b - Newly merged workspace list.
 * @return ``true`` when both lists are element-wise value-equal.
 */
function workspacesEqual(a: AntiochWorkspace[], b: AntiochWorkspace[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.name !== y.name ||
      x.hostname !== y.hostname ||
      x.ipAddress !== y.ipAddress ||
      x.createdAt !== y.createdAt ||
      x.runningAt !== y.runningAt ||
      x.stoppedAt !== y.stoppedAt ||
      x.erroredAt !== y.erroredAt ||
      x.version !== y.version ||
      x.failureReason !== y.failureReason ||
      x.scheduledShutdownAt !== y.scheduledShutdownAt ||
      x.state !== y.state
    ) {
      return false;
    }
  }
  return true;
}

// Dialog state interfaces
interface DialogState<T> {
  isOpen: boolean;
  workspace: T | null;
  isLoading: boolean;
}

export interface WorkspaceDialogs {
  stop: DialogState<Workspace>;
  terminate: DialogState<Workspace>;
  confirmStop: () => Promise<void>;
  cancelStop: () => void;
  confirmTerminate: () => Promise<void>;
  cancelTerminate: () => void;
}

// localStorage key for the staff "reveal customer image names" preference.
const REVEAL_IMAGE_NAMES_KEY = 'antioch:revealImageNames';

export interface WorkspaceContextValue {
  // State
  workspaces: AntiochWorkspace[];
  workspacesFetched: boolean;
  connectedWorkspace: { id: string; name: string; version?: string } | null;
  streamTarget: WorkspaceStreamTarget | null;
  /**
   * True while we still expect a saved workspace from a prior session
   * to land. Specifically: there is a saved-workspace-id in
   * ``localStorage`` AND no live ``connectedWorkspace`` yet AND
   * either the Rome workspaces fetch is still in flight OR the
   * saved id is currently a RUNNING workspace in the fetched list
   * (the restore effect is about to call ``handleViewWorkspace``).
   *
   * Consumers (notably :class:`StreamOnlyWindow`) use this to keep
   * a "Connecting..." placeholder on screen across the refresh
   * hydration window instead of flashing the bare "Select a
   * workspace" empty state for a few hundred milliseconds before
   * the saved workspace finishes rehydrating.
   */
  isRestoringWorkspace: boolean;
  isStartingWorkspace: boolean;
  restartingWorkspaceId: string | null;
  availableVersions: WorkspaceVersion[];
  /** Full image catalog: central antioch-sim versions plus the org's custom images */
  availableImages: WorkspaceImage[];
  /** Set when the org's custom-image listing failed; central versions are still served */
  orgImagesError: string | null;
  versionsLoading: boolean;
  /**
   * Antioch-staff preference: ask Rome to reveal friendly customer names for
   * images the caller's org does not own. Persisted locally; Rome enforces the
   * staff gate server-side, so a non-staff session setting it has no effect.
   */
  revealImageNames: boolean;
  setRevealImageNames: (value: boolean) => void;

  // Startup progress tracking
  progressMap: Record<string, WorkspaceProgressStep[]>;
  fetchProgressForWorkspace: (workspaceId: string) => Promise<void>;

  // Local workspace config for dev mode
  localWorkspace: AntiochWorkspace | null;
  localWorkspaceId: string;
  isLocalDev: boolean;

  // Actions
  viewWorkspace: (workspace: Workspace) => void;
  disconnect: () => void;
  startWorkspace: (name: string, version: string, image?: string) => Promise<void>;
  restartWorkspace: (workspace: AntiochWorkspace, version?: string, image?: string) => Promise<void>;
  stopWorkspace: (workspace: Workspace) => void;
  terminateWorkspace: (workspace: Workspace) => void;
  // Dialog state (for confirmation modals)
  dialogs: WorkspaceDialogs;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

interface WorkspaceProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages workspace state and operations.
 * Handles fetching, creating, stopping, and terminating workspaces.
 */
export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { broadcastWorkspaceConnected, broadcastWorkspaceDisconnected, releaseStreamLock } = useSharedState();

  // Workspace list state
  const [workspaces, setWorkspaces] = useState<AntiochWorkspace[]>([]);
  const [workspacesFetched, setWorkspacesFetched] = useState(false);
  const workspacesLastSeenMsByIdRef = useRef<Record<string, number>>({});
  // Tracks when we made an optimistic state change (start, stop, restart, terminate)
  // Separate from lastSeenMs which is updated on every poll
  const optimisticUpdateMsByIdRef = useRef<Record<string, number>>({});

  // Stream target: workspace to connect to (null = no active workspace)
  const [streamTarget, setStreamTarget] = useState<WorkspaceStreamTarget | null>(null);

  // Currently connected workspace info (for display in overlay/toolbar)
  const [connectedWorkspace, setConnectedWorkspace] = useState<{ id: string; name: string; version?: string } | null>(
    null
  );

  // Loading states
  const [isStartingWorkspace, setIsStartingWorkspace] = useState(false);
  const [restartingWorkspaceId, setRestartingWorkspaceId] = useState<string | null>(null);

  // Available workspace versions
  const [availableVersions, setAvailableVersions] = useState<WorkspaceVersion[]>([]);
  const [availableImages, setAvailableImages] = useState<WorkspaceImage[]>([]);
  const [orgImagesError, setOrgImagesError] = useState<string | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Monotonic counter so only the latest-issued image fetch writes state.
  // Declared here (before the reveal setter) so disabling reveal can invalidate
  // any in-flight fetch immediately, not only when the refetch is later issued.
  const imageFetchSeqRef = useRef(0);

  // Staff reveal preference for custom-image friendly names, persisted locally.
  // Reading localStorage can throw (Safari private mode, blocked storage), so
  // guard the initializer the same way the setter below guards writes.
  const [revealImageNames, setRevealImageNamesState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(REVEAL_IMAGE_NAMES_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const setRevealImageNames = useCallback((value: boolean) => {
    try {
      localStorage.setItem(REVEAL_IMAGE_NAMES_KEY, String(value));
    } catch {
      // localStorage may be unavailable; the in-memory preference still applies
    }
    if (!value) {
      // Fail-opaque: invalidate any in-flight reveal-on fetch immediately (so a
      // response captured under the old reveal state can't repopulate names
      // after the toggle is off) and drop revealed names from the cache now.
      // The refetch triggered by the preference change restores owner names.
      imageFetchSeqRef.current += 1;
      setAvailableImages((prev) => prev.map((img) => ({ ...img, display_name: null })));
    }
    setRevealImageNamesState(value);
  }, []);

  // Startup progress tracking (workspace_id -> progress steps)
  const [progressMap, setProgressMap] = useState<Record<string, WorkspaceProgressStep[]>>({});

  // Workspace IDs with an in-flight start/restart API call. Progress polling
  // skips these to avoid fetching stale data before Rome clears it.
  const pendingStartRef = useRef<Set<string>>(new Set());

  // Ref to current workspaces so the progress polling effect can read the latest
  // value without needing workspaces in its dependency array (avoids teardown on
  // every workspace poll cycle which discards in-flight progress fetches)
  const workspacesRef = useRef(workspaces);
  workspacesRef.current = workspaces;

  const runAuthorizedWorkspaceRequest = useCallback(
    async (
      toastTitle: string,
      submit: (
        token: string,
        workspaceAuthDeviceCode: string,
        workspaceAuthInterval: number
      ) => Promise<WorkspaceMutationResponse>
    ): Promise<WorkspaceMutationResponse> => {
      const approvalWindow = openWorkspaceAuthWindow();
      const toastId = toast.loading(toastTitle, {
        description: 'Preparing Auth0 approval...',
        duration: Infinity,
      });

      try {
        const token = await getAccessTokenSilently();
        const workspaceAuth = await beginWorkspaceAuthorization({
          accessToken: token,
          approvalWindow,
          onApprovalUrl: (url, userCode) => {
            toast.loading(toastTitle, {
              id: toastId,
              description: approvalWindow
                ? 'Complete the Auth0 approval in the new window.'
                : 'Popup blocked. Open the Auth0 approval to continue.',
              duration: Infinity,
              action: approvalWindow
                ? undefined
                : {
                    label: userCode ? `Open (${userCode})` : 'Open',
                    onClick: () => window.open(url, '_blank'),
                  },
            });
          },
        });

        const deadlineMs = Date.now() + workspaceAuth.expiresIn * 1000;
        let intervalSeconds = Math.max(1, workspaceAuth.interval);

        while (Date.now() < deadlineMs) {
          const response = await submit(token, workspaceAuth.deviceCode, intervalSeconds);
          if (response.status !== 202) {
            approvalWindow?.close();
            toast.dismiss(toastId);
            return response;
          }

          const pending = response.data as WorkspaceAuthPendingResponse | null;
          intervalSeconds = Math.max(1, pending?.data?.interval ?? intervalSeconds);
          toast.loading(toastTitle, {
            id: toastId,
            description: 'Waiting for Auth0 approval...',
            duration: Infinity,
          });

          const remainingMs = deadlineMs - Date.now();
          await sleep(Math.min(intervalSeconds * 1000, Math.max(0, remainingMs)));
        }

        throw new WorkspaceAuthError('start_failed', 'Workspace authorization expired.');
      } catch (error) {
        approvalWindow?.close();
        const description =
          error instanceof WorkspaceAuthError
            ? error.message
            : 'The workspace credential could not be approved. Please try again.';
        toast.error('Workspace authorization failed.', {
          id: toastId,
          description,
          duration: 6000,
        });
        throw error;
      }
    },
    [getAccessTokenSilently]
  );

  // Dialog states for stop confirmation
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [workspaceToStop, setWorkspaceToStop] = useState<Workspace | null>(null);
  const [isStoppingWorkspace, setIsStoppingWorkspace] = useState(false);

  // Dialog states for terminate confirmation
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [workspaceToTerminate, setWorkspaceToTerminate] = useState<Workspace | null>(null);
  const [isTerminatingWorkspace, setIsTerminatingWorkspace] = useState(false);

  // Fetch control refs
  const initialFetchInProgressRef = useRef(false);
  const fetchErrorCountRef = useRef(0);
  const lastFetchTimeRef = useRef(0);

  // Handle viewing a workspace - switch to Workspace tab and update target
  const handleViewWorkspace = useCallback(
    (workspace: Workspace) => {
      Sentry.addBreadcrumb({
        category: 'workspace',
        message: 'view workspace',
        data: { workspaceId: workspace.id, name: workspace.name },
      });
      updateWorkspaceContext({
        id: workspace.id,
        name: workspace.name,
        state: workspace.state,
        version: workspace.version,
        createdAt: workspace.createdAt,
        runningAt: workspace.runningAt,
      });
      // Check if this is the local dev workspace
      if (workspace.id === LOCAL_WORKSPACE_ID) {
        setStreamTarget(getLocalStreamTarget());
        setConnectedWorkspace({ id: workspace.id, name: workspace.name, version: workspace.version });
      } else {
        // Both hostname and ipAddress are required for cloud workspaces
        // ipAddress is used for WebRTC UDP media (Cloudflare doesn't proxy UDP)
        const newTarget =
          workspace.hostname && workspace.ipAddress
            ? buildTargetFromWorkspace(workspace.hostname, workspace.ipAddress)
            : null;
        setStreamTarget(newTarget);
        setConnectedWorkspace({ id: workspace.id, name: workspace.name, version: workspace.version });
      }

      // Save selected workspace to localStorage for persistence across refreshes and tabs
      writeSelectedWorkspaceId(workspace.id);

      // Broadcast to other tabs
      broadcastWorkspaceConnected(workspace.id, workspace.name);
    },
    [broadcastWorkspaceConnected]
  );

  // Handle disconnecting from a workspace
  const handleDisconnect = useCallback(() => {
    Sentry.addBreadcrumb({ category: 'workspace', message: 'disconnect workspace' });
    clearAllContexts();
    setStreamTarget(null);
    setConnectedWorkspace(null);
    clearSelectedWorkspaceId();

    // Release stream lock and broadcast to other tabs
    releaseStreamLock();
    broadcastWorkspaceDisconnected();
  }, [releaseStreamLock, broadcastWorkspaceDisconnected]);

  // Handle terminating a workspace - show confirmation dialog
  const handleTerminateWorkspace = useCallback((workspace: Workspace) => {
    setWorkspaceToTerminate(workspace);
    setShowTerminateDialog(true);
  }, []);

  // Confirm terminating a workspace
  const handleTerminateConfirm = useCallback(async () => {
    if (!workspaceToTerminate) return;

    setIsTerminatingWorkspace(true);
    setShowTerminateDialog(false);

    // Disconnect if we're connected to the workspace being terminated
    if (connectedWorkspace?.id === workspaceToTerminate.id) {
      handleDisconnect();
    }

    try {
      // Optimistically mark as terminating and keep in the list to prevent flicker
      const nowMs = Date.now();
      workspacesLastSeenMsByIdRef.current[workspaceToTerminate.id] = nowMs;
      optimisticUpdateMsByIdRef.current[workspaceToTerminate.id] = nowMs;
      setWorkspaces((prevWorkspaces) =>
        prevWorkspaces.map((w) => (w.id === workspaceToTerminate.id ? { ...w, state: WorkspaceState.TERMINATING } : w))
      );

      const token = await getAccessTokenSilently();
      const response = await Sentry.startSpan(
        {
          name: 'Terminate Workspace',
          op: 'workspace.terminate',
          attributes: { 'workspace.id': workspaceToTerminate.id },
        },
        () => terminateWorkspace(token, workspaceToTerminate.id)
      );

      if (response.status === 200) {
        toast.success(`Workspace "${workspaceToTerminate.name}" has been deleted successfully.`);
      } else if (response.status === 404) {
        console.error('[WorkspaceContext] Workspace not found or does not match org:', response);
        // Workspace is gone or invalid, so remove it locally
        setWorkspaces((prevWorkspaces) => prevWorkspaces.filter((w) => w.id !== workspaceToTerminate.id));
        toast.error('Failed to delete workspace.', {
          description: response.data?.message || 'Workspace not found or does not belong to your organization.',
        });
      } else {
        console.error('[WorkspaceContext] Failed to terminate workspace:', response);
        // Revert optimistic state on failure
        setWorkspaces((prevWorkspaces) =>
          prevWorkspaces.map((w) =>
            w.id === workspaceToTerminate.id ? { ...w, state: workspaceToTerminate.state } : w
          )
        );
        toast.error('Failed to delete workspace.', {
          description: `Server returned status ${response.status}.`,
        });
      }
    } catch (error) {
      console.error('[WorkspaceContext] Error terminating workspace:', error);
      captureOrBreadcrumbNetworkError(
        error,
        {
          category: 'workspace',
          message: 'network error during terminate',
          data: { workspace_id: workspaceToTerminate.id },
        },
        { tags: { workspace_id: workspaceToTerminate.id } }
      );
      // Revert optimistic state on error
      setWorkspaces((prevWorkspaces) =>
        prevWorkspaces.map((w) => (w.id === workspaceToTerminate.id ? { ...w, state: workspaceToTerminate.state } : w))
      );
      toast.error('An error occurred while deleting the workspace.', {
        description: 'Please try again or contact support if the issue persists.',
      });
    } finally {
      setIsTerminatingWorkspace(false);
      setWorkspaceToTerminate(null);
    }
  }, [workspaceToTerminate, connectedWorkspace, handleDisconnect, getAccessTokenSilently]);

  // Cancel terminating a workspace
  const handleTerminateCancel = useCallback(() => {
    setShowTerminateDialog(false);
    setWorkspaceToTerminate(null);
  }, []);

  // Handle stopping a workspace - show confirmation dialog
  const handleStopWorkspace = useCallback((workspace: Workspace) => {
    setWorkspaceToStop(workspace);
    setShowStopDialog(true);
  }, []);

  // Confirm stopping a workspace
  const handleStopConfirm = useCallback(async () => {
    if (!workspaceToStop) return;

    setIsStoppingWorkspace(true);
    setShowStopDialog(false);

    // Disconnect if we're connected to the workspace being stopped
    if (connectedWorkspace?.id === workspaceToStop.id) {
      handleDisconnect();
    }

    try {
      // Optimistically mark as stopping and keep in the list to prevent flicker
      const nowMs = Date.now();
      workspacesLastSeenMsByIdRef.current[workspaceToStop.id] = nowMs;
      optimisticUpdateMsByIdRef.current[workspaceToStop.id] = nowMs;
      setWorkspaces((prevWorkspaces) =>
        prevWorkspaces.map((w) => (w.id === workspaceToStop.id ? { ...w, state: WorkspaceState.STOPPING } : w))
      );

      const token = await getAccessTokenSilently();
      const response = await Sentry.startSpan(
        { name: 'Stop Workspace', op: 'workspace.stop', attributes: { 'workspace.id': workspaceToStop.id } },
        () => stopWorkspace(token, workspaceToStop.id)
      );

      if (response.status === 200) {
        toast.success(`Workspace "${workspaceToStop.name}" has been paused successfully.`);
      } else if (response.status === 404) {
        console.error('[WorkspaceContext] Workspace not found or does not match org:', response);
        // Workspace is gone or invalid, so remove it locally
        setWorkspaces((prevWorkspaces) => prevWorkspaces.filter((w) => w.id !== workspaceToStop.id));
        toast.error('Failed to pause workspace.', {
          description: response.data?.message || 'Workspace not found or does not belong to your organization.',
        });
      } else {
        console.error('[WorkspaceContext] Failed to stop workspace:', response);
        // Revert optimistic state on failure
        setWorkspaces((prevWorkspaces) =>
          prevWorkspaces.map((w) => (w.id === workspaceToStop.id ? { ...w, state: workspaceToStop.state } : w))
        );
        toast.error('Failed to pause workspace.', {
          description: `Server returned status ${response.status}.`,
        });
      }
    } catch (error) {
      console.error('[WorkspaceContext] Error stopping workspace:', error);
      captureOrBreadcrumbNetworkError(
        error,
        {
          category: 'workspace',
          message: 'network error during stop',
          data: { workspace_id: workspaceToStop.id },
        },
        { tags: { workspace_id: workspaceToStop.id } }
      );
      // Revert optimistic state on error
      setWorkspaces((prevWorkspaces) =>
        prevWorkspaces.map((w) => (w.id === workspaceToStop.id ? { ...w, state: workspaceToStop.state } : w))
      );
      toast.error('An error occurred while pausing the workspace.', {
        description: 'Please try again or contact support if the issue persists.',
      });
    } finally {
      setIsStoppingWorkspace(false);
      setWorkspaceToStop(null);
    }
  }, [workspaceToStop, connectedWorkspace, handleDisconnect, getAccessTokenSilently]);

  // Cancel stopping a workspace
  const handleStopCancel = useCallback(() => {
    setShowStopDialog(false);
    setWorkspaceToStop(null);
  }, []);

  // Handle starting a new workspace
  const handleStartNewWorkspace = useCallback(
    async (workspaceName: string, version: string, image?: string) => {
      setIsStartingWorkspace(true);

      try {
        const response = await runAuthorizedWorkspaceRequest(
          'Authorize workspace access',
          (token, workspaceAuthDeviceCode, workspaceAuthInterval) =>
            Sentry.startSpan({ name: 'Start Workspace', op: 'workspace.start' }, () =>
              startWorkspace(token, {
                workspaceName,
                version: version || undefined,
                image,
                workspaceAuthDeviceCode,
                workspaceAuthInterval,
              })
            )
        );

        const newWorkspaceData = getWorkspaceStartData(response);
        if (newWorkspaceData) {
          // Optimistically add the new workspace to the list immediately
          const optimisticWorkspace: AntiochWorkspace = {
            id: newWorkspaceData.workspace_id,
            name: newWorkspaceData.name || workspaceName,
            hostname: '', // Will be populated when workspace is ready
            createdAt: Math.floor(Date.now() / 1000),
            version: newWorkspaceData.version || version,
            image: newWorkspaceData.image || image,
            state: WorkspaceState.STARTING,
          };

          const nowMs = Date.now();
          workspacesLastSeenMsByIdRef.current[optimisticWorkspace.id] = nowMs;
          optimisticUpdateMsByIdRef.current[optimisticWorkspace.id] = nowMs;

          setWorkspaces((prev) => {
            // Check if workspace already exists (shouldn't, but be safe)
            const exists = prev.some((w) => w.id === optimisticWorkspace.id);
            if (exists) return prev;
            return [optimisticWorkspace, ...prev];
          });

          toast.success('Workspace starting.', {
            description: `"${workspaceName}" will be ready shortly.`,
          });
        } else if (response.status === 429) {
          // Workspace limit reached
          toast.error('Workspace limit reached', {
            description:
              (response.data as unknown as { message?: string })?.message ||
              'Your organization has reached its workspace limit. Pause or delete an existing workspace to start a new one.',
          });
        } else {
          console.error('[WorkspaceContext] Failed to start workspace:', response);
          toast.error('Failed to start workspace.', {
            description: `Server returned status ${response.status}. Please try again or contact support.`,
          });
        }
      } catch (error) {
        if (error instanceof WorkspaceAuthError) {
          return;
        }
        console.error('[WorkspaceContext] Error starting new workspace:', error);
        captureOrBreadcrumbNetworkError(error, {
          category: 'workspace',
          message: 'network error during start',
        });
        toast.error('An unexpected error occurred while starting the workspace.', {
          description: 'Please try again or contact support if the issue persists.',
        });
      } finally {
        setIsStartingWorkspace(false);
      }
    },
    [runAuthorizedWorkspaceRequest]
  );

  // Handle restarting a stopped workspace
  const handleRestartWorkspace = useCallback(
    async (workspace: AntiochWorkspace, version?: string, image?: string) => {
      setRestartingWorkspaceId(workspace.id);

      // Optimistically update state and version before the API call
      const nowMs = Date.now();
      workspacesLastSeenMsByIdRef.current[workspace.id] = nowMs;
      optimisticUpdateMsByIdRef.current[workspace.id] = nowMs;
      setWorkspaces((prev) =>
        prev.map((w) =>
          w.id === workspace.id
            ? {
                ...w,
                state: WorkspaceState.STARTING,
                version: version || w.version,
                image: image || w.image,
                failureReason: null,
              }
            : w
        )
      );

      // Clear stale progress and suppress progress polling until the API call
      // completes (Rome needs to clear progress before we start reading it)
      setProgressMap((prev) => {
        const next = { ...prev };
        delete next[workspace.id];
        return next;
      });
      pendingStartRef.current.add(workspace.id);

      try {
        const response = await runAuthorizedWorkspaceRequest(
          'Authorize workspace restart',
          (token, workspaceAuthDeviceCode, workspaceAuthInterval) =>
            Sentry.startSpan(
              { name: 'Restart Workspace', op: 'workspace.restart', attributes: { 'workspace.id': workspace.id } },
              () =>
                restartWorkspace(token, workspace.id, version, image, workspaceAuthDeviceCode, workspaceAuthInterval)
            )
        );

        if (response.status === 200 && response.data) {
          toast.success('Workspace restarting.', {
            description: `${workspace.name} is being started.`,
          });
        } else if (response.status === 429) {
          // Workspace limit reached
          toast.error('Workspace limit reached', {
            description:
              (response.data as unknown as { message?: string })?.message ||
              'Your organization has reached its workspace limit. Pause or delete an existing workspace to start a new one.',
          });
        } else {
          toast.error('Failed to restart workspace.', {
            description: response.data?.message || `Status ${response.status}`,
          });
        }
      } catch (error) {
        if (error instanceof WorkspaceAuthError) {
          setWorkspaces((prev) =>
            prev.map((w) => (w.id === workspace.id ? { ...w, state: workspace.state, version: workspace.version } : w))
          );
          return;
        }
        console.error('[WorkspaceContext] Error restarting workspace:', error);
        captureOrBreadcrumbNetworkError(
          error,
          {
            category: 'workspace',
            message: 'network error during restart',
            data: { workspace_id: workspace.id },
          },
          { tags: { workspace_id: workspace.id } }
        );
        toast.error('An unexpected error occurred while restarting the workspace.');
      } finally {
        pendingStartRef.current.delete(workspace.id);
        setRestartingWorkspaceId(null);
      }
    },
    [runAuthorizedWorkspaceRequest]
  );

  // Fetch user workspaces from Rome API with backoff on errors
  const fetchWorkspaces = useCallback(
    async (isInitialLoad = false) => {
      // Prevent rapid-fire requests (minimum 2 seconds between calls)
      const now = Date.now();
      if (!isInitialLoad && now - lastFetchTimeRef.current < 2000) {
        return;
      }
      lastFetchTimeRef.current = now;

      try {
        const token = await getAccessTokenSilently();
        const response = await getUserWorkspaces(token);

        if (response.status === 200 && response.data && response.data.data) {
          const transformedWorkspaces = transformWorkspacesForUI(response.data.data);
          const nowMs = Date.now();
          for (const w of transformedWorkspaces) {
            workspacesLastSeenMsByIdRef.current[w.id] = nowMs;
          }
          // Detect STARTING -> ERRORED transitions before updating state
          // (side effects like toasts must not run inside the state updater)
          const currentWorkspaces = workspacesRef.current;
          for (const serverWs of transformedWorkspaces) {
            if (serverWs.state === WorkspaceState.ERRORED) {
              const prevWs = currentWorkspaces.find((p) => p.id === serverWs.id);
              if (prevWs && prevWs.state === WorkspaceState.STARTING) {
                toast.error(`Workspace "${serverWs.name}" failed to start`, {
                  description: serverWs.failureReason || 'An unexpected error occurred during startup',
                });
              }
            }
          }

          setWorkspaces((prev) => {
            const next = mergeWorkspaces({
              prev,
              server: transformedWorkspaces,
              lastSeenMsById: workspacesLastSeenMsByIdRef.current,
              optimisticMsById: optimisticUpdateMsByIdRef.current,
              nowMs,
            });
            // Keep the prev reference on no-op merges so the context value
            // identity stays stable and consumers don't re-render every poll.
            return workspacesEqual(prev, next) ? prev : next;
          });
          fetchErrorCountRef.current = 0;
        }
      } catch (error) {
        fetchErrorCountRef.current++;
        // Only log first few errors to avoid console spam
        if (fetchErrorCountRef.current <= 3) {
          console.error('[WorkspaceContext] Error fetching workspaces:', error);
        } else if (fetchErrorCountRef.current === 4) {
          console.error('[WorkspaceContext] Suppressing further workspace fetch errors...');
        }
      }
    },
    [getAccessTokenSilently]
  );

  // Fetch the workspace image catalog. The version dropdown derives from the
  // central entries (always served, even when the org's custom-image listing
  // fails - Rome surfaces that as org_images_error instead of an empty list).
  const fetchVersions = useCallback(async () => {
    // Guard against out-of-order responses: rapid reveal toggling can leave two
    // fetches in flight, and a slower reveal-on response must not clobber a
    // newer reveal-off one (which would re-show customer names). Only the
    // latest-issued fetch is allowed to write state.
    const seq = imageFetchSeqRef.current + 1;
    imageFetchSeqRef.current = seq;
    setVersionsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await getWorkspaceImages(token, revealImageNames);
      if (seq !== imageFetchSeqRef.current) return; // superseded by a newer fetch
      if (response.status === 200 && response.data?.data) {
        const images = response.data.data;
        setAvailableImages(images);
        setOrgImagesError(response.data.org_images_error ?? null);
        setAvailableVersions(
          images
            .filter((img) => img.source === 'central')
            .map((img) => ({
              version: img.version,
              image_tag: img.image_tag,
              image: `${img.repository}:${img.image_tag}`,
              released_at: img.released_at,
            }))
        );
      } else {
        // Deploy-skew fallback: a webapp deployed ahead of Rome gets a 404
        // from /workspace/images. Every Rome still serves the deprecated
        // /workspace/versions, so the version dropdown keeps working; the
        // image picker stays hidden until the catalog endpoint exists.
        const versions = await getWorkspaceVersions(token);
        if (seq !== imageFetchSeqRef.current) return;
        if (versions.status === 200 && versions.data?.data) {
          setAvailableVersions(versions.data.data);
        }
      }
    } catch {
      // Versions are non-critical; silently ignore errors
    } finally {
      if (seq === imageFetchSeqRef.current) setVersionsLoading(false);
    }
  }, [getAccessTokenSilently, revealImageNames]);

  // Initial workspace fetch - runs only once when authenticated
  useEffect(() => {
    if (isAuthenticated && !workspacesFetched && !initialFetchInProgressRef.current) {
      initialFetchInProgressRef.current = true;
      fetchWorkspaces(true).finally(() => setWorkspacesFetched(true));
      fetchVersions();
    }
  }, [isAuthenticated, workspacesFetched, fetchWorkspaces, fetchVersions]);

  // Re-fetch the image catalog when the reveal preference changes so friendly
  // names appear/disappear without a reload. Skips the initial mount (the
  // effect above does the first fetch).
  const revealInitializedRef = useRef(false);
  useEffect(() => {
    if (!revealInitializedRef.current) {
      revealInitializedRef.current = true;
      return;
    }
    if (isAuthenticated) {
      fetchVersions();
    }
    // fetchVersions closes over revealImageNames, so its identity changes when
    // the preference flips -- that is what re-triggers this effect.
  }, [isAuthenticated, fetchVersions]);

  // Snapshot the saved-workspace id at mount time so consumers can
  // know we're mid-restore from the very first render -- before the
  // restore effect below has had a chance to run, and before the
  // Rome workspaces fetch has resolved. Read inside ``useState``'s
  // lazy initializer so we only hit ``localStorage`` once at mount;
  // re-reads happen via the same key in the restore effect, and
  // ``isRestoringWorkspace`` self-clears when ``connectedWorkspace``
  // lands or the saved id falls out of the running set.
  const [savedWorkspaceIdAtMount] = useState<string | null>(() => {
    return readSelectedWorkspaceId();
  });

  /**
   * Whether a saved workspace from a prior session is still expected
   * to land. Drives the "don't flash the empty state during the
   * refresh hydration window" UX on ``/workspace``. See the docstring
   * on :attr:`WorkspaceContextValue.isRestoringWorkspace` for the
   * full state machine.
   */
  const isRestoringWorkspace = useMemo<boolean>(() => {
    if (connectedWorkspace !== null) return false;
    if (!savedWorkspaceIdAtMount) return false;
    // Local dev: ``handleViewWorkspace`` runs as soon as
    // ``localWorkspace`` is materialized (always at module load), so
    // restoration is in flight until ``connectedWorkspace`` lands.
    if (savedWorkspaceIdAtMount === LOCAL_WORKSPACE_ID) {
      return isLocalDevelopment() && localWorkspace !== null;
    }
    // Remote workspaces: wait for the Rome list, then confirm the
    // saved id is still a running workspace. If the fetch has
    // resolved and the id is gone (terminated / stopped between
    // sessions), the restore effect is about to clear the key --
    // surface the empty state instead of a perpetual spinner.
    if (!workspacesFetched) return true;
    return workspaces.some((w) => w.id === savedWorkspaceIdAtMount && w.state === WorkspaceState.RUNNING);
  }, [connectedWorkspace, savedWorkspaceIdAtMount, workspacesFetched, workspaces]);

  // Restore workspace from localStorage after initial fetch.
  // The local development workspace is a synthetic hardcoded object that
  // doesn't come from the Rome API, so it can be restored immediately
  // without waiting for workspacesFetched -- this eliminates a visible
  // "disconnected" flash on page refresh in local dev.
  useEffect(() => {
    if (connectedWorkspace) return;

    const savedWorkspaceId = readSelectedWorkspaceId();
    if (!savedWorkspaceId) return;

    // Fast path: local workspace restore skips the Rome API gate
    if (savedWorkspaceId === LOCAL_WORKSPACE_ID && isLocalDevelopment() && localWorkspace) {
      handleViewWorkspace(localWorkspace);
      return;
    }

    // Remote workspaces need the API fetch to complete first
    if (!workspacesFetched) return;

    // Find workspace in fetched workspaces
    const savedWorkspace = workspaces.find((w) => w.id === savedWorkspaceId && w.state === WorkspaceState.RUNNING);

    if (savedWorkspace) {
      const workspaceForView: Workspace = {
        id: savedWorkspace.id,
        name: savedWorkspace.name,
        hostname: savedWorkspace.hostname || '',
        ipAddress: savedWorkspace.ipAddress,
        state: savedWorkspace.state,
        createdAt: savedWorkspace.createdAt,
        version: savedWorkspace.version,
      };
      handleViewWorkspace(workspaceForView);
    } else {
      // Workspace no longer exists or not running, clear localStorage
      clearSelectedWorkspaceId();
    }
  }, [workspacesFetched, workspaces, connectedWorkspace, handleViewWorkspace]);

  // Auto-refresh workspaces with exponential backoff on errors
  useEffect(() => {
    if (!isAuthenticated) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextFetch = () => {
      // Exponential backoff: 10s, 20s, 40s, max 60s
      const backoffMs = Math.min(10000 * 2 ** fetchErrorCountRef.current, 60000);
      timeoutId = setTimeout(() => {
        // Skip the network round-trip while the tab is backgrounded; the
        // visibility handler below refreshes the instant the user returns.
        if (!document.hidden) fetchWorkspaces(false);
        scheduleNextFetch();
      }, backoffMs);
    };

    const handleVisibility = () => {
      if (!document.hidden) fetchWorkspaces(false);
    };

    scheduleNextFetch();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, fetchWorkspaces]);

  // Derive a stable boolean for whether any workspace is STARTING so the
  // progress polling effect only re-triggers when the polling speed needs
  // to change, not on every workspace list refresh
  const hasStartingWorkspace = workspaces.some((w) => w.state === WorkspaceState.STARTING);

  // Poll progress only for STARTING workspaces to avoid N+1 API calls.
  // Non-starting workspace progress is fetched on-demand via fetchProgressForWorkspace.
  useEffect(() => {
    if (!isAuthenticated || !workspacesFetched || !hasStartingWorkspace) return;

    let cancelled = false;

    const fetchStartingProgress = async () => {
      // Don't poll progress while the tab is hidden; we refresh on return.
      if (cancelled || document.hidden) return;

      const currentWorkspaces = workspacesRef.current;
      const pending = pendingStartRef.current;

      try {
        const token = await getAccessTokenSilently();

        const startingWorkspaces = currentWorkspaces.filter(
          (w) => w.state === WorkspaceState.STARTING && !pending.has(w.id)
        );

        const results: Record<string, WorkspaceProgressStep[]> = {};
        await Promise.allSettled(
          startingWorkspaces.map(async (w) => {
            const resp = await getWorkspaceProgress(token, w.id);
            if (resp.status === 200 && resp.data?.data?.steps) {
              results[w.id] = resp.data.data.steps;
            }
          })
        );

        if (!cancelled) {
          setProgressMap((prev) => ({ ...prev, ...results }));
        }
      } catch {
        // Progress polling is non-critical; silently ignore errors
      }
    };

    fetchStartingProgress();
    const intervalId = setInterval(fetchStartingProgress, 4000);
    const handleVisibility = () => {
      if (!document.hidden) fetchStartingProgress();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, workspacesFetched, hasStartingWorkspace, getAccessTokenSilently]);

  // Fetch progress on-demand for a single workspace (used by startup history modal)
  const fetchProgressForWorkspace = useCallback(
    async (workspaceId: string) => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await getWorkspaceProgress(token, workspaceId);
        if (resp.status === 200 && resp.data?.data) {
          const steps = resp.data.data.steps ?? [];
          setProgressMap((prev) => ({ ...prev, [workspaceId]: steps }));
        }
      } catch {
        Sentry.addBreadcrumb({
          category: 'workspace',
          message: 'on-demand progress fetch failed',
          level: 'warning',
          data: { workspaceId },
        });
      }
    },
    [getAccessTokenSilently]
  );

  // Memoized context value to prevent unnecessary re-renders
  const value = useMemo<WorkspaceContextValue>(
    () => ({
      // State
      workspaces,
      workspacesFetched,
      connectedWorkspace,
      streamTarget,
      isRestoringWorkspace,
      isStartingWorkspace,
      restartingWorkspaceId,
      availableVersions,
      availableImages,
      orgImagesError,
      versionsLoading,
      revealImageNames,
      setRevealImageNames,
      progressMap,
      fetchProgressForWorkspace,

      // Local workspace config
      localWorkspace,
      localWorkspaceId: LOCAL_WORKSPACE_ID,
      isLocalDev: isLocalDevelopment(),

      // Actions
      viewWorkspace: handleViewWorkspace,
      disconnect: handleDisconnect,
      startWorkspace: handleStartNewWorkspace,
      restartWorkspace: handleRestartWorkspace,
      stopWorkspace: handleStopWorkspace,
      terminateWorkspace: handleTerminateWorkspace,

      // Dialog state
      dialogs: {
        stop: {
          isOpen: showStopDialog,
          workspace: workspaceToStop,
          isLoading: isStoppingWorkspace,
        },
        terminate: {
          isOpen: showTerminateDialog,
          workspace: workspaceToTerminate,
          isLoading: isTerminatingWorkspace,
        },
        confirmStop: handleStopConfirm,
        cancelStop: handleStopCancel,
        confirmTerminate: handleTerminateConfirm,
        cancelTerminate: handleTerminateCancel,
      },
    }),
    [
      workspaces,
      workspacesFetched,
      connectedWorkspace,
      streamTarget,
      isRestoringWorkspace,
      isStartingWorkspace,
      restartingWorkspaceId,
      availableVersions,
      availableImages,
      orgImagesError,
      versionsLoading,
      revealImageNames,
      setRevealImageNames,
      progressMap,
      fetchProgressForWorkspace,
      showStopDialog,
      workspaceToStop,
      isStoppingWorkspace,
      showTerminateDialog,
      workspaceToTerminate,
      isTerminatingWorkspace,
      handleViewWorkspace,
      handleDisconnect,
      handleStartNewWorkspace,
      handleRestartWorkspace,
      handleStopWorkspace,
      handleTerminateWorkspace,
      handleStopConfirm,
      handleStopCancel,
      handleTerminateConfirm,
      handleTerminateCancel,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/**
 * Hook to access workspace context.
 * Must be used within a WorkspaceProvider.
 */
export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
